/**
 * Ядро блокировки приложения (код-пароль + биометрия).
 *
 * Unlock-тикет живёт ТОЛЬКО в памяти модуля: localStorage сознательно не
 * используется — холодный старт всегда требует код, а чтение localStorage
 * из DevTools тикета не даёт.
 */

// Сколько можно отсутствовать (фон/другое приложение) без повторного кода.
export const GRACE_MS = 120_000;

let unlockTicket = null;

// Промис-шлюз для axios-интерцептора: 428 -> ждём, пока юзер введёт код.
// Ожидание разрешается булевом: true — код введён (или пин сняли, тикет не
// нужен), false — человек закрыл окно ввода. Раньше здесь был resolve() без
// значения, потому что отказаться было негде: полноэкранный замок кнопки
// «отмена» не имеет. У шаг-ап окна она есть, и без этого различия отменённый
// запрос висел бы в ожидании вечно — вместе со спиннером на кнопке.
const unlockWaiters = [];

/** Будит запросы, ждущие разблокировки (в т.ч. когда пин сняли — тикета нет). */
export const releaseUnlockWaiters = () => {
  for (const resolve of unlockWaiters.splice(0)) resolve(true);
};

/** Отпускает ожидающие запросы отказом: человек закрыл окно ввода кода. */
export const cancelUnlockWaiters = () => {
  for (const resolve of unlockWaiters.splice(0)) resolve(false);
};

export const getUnlockTicket = () => unlockTicket;
export const setUnlockTicket = (ticket) => {
  unlockTicket = typeof ticket === "string" && ticket ? ticket : null;
  if (unlockTicket) releaseUnlockWaiters();
};
export const clearUnlockTicket = () => {
  unlockTicket = null;
};

export const waitForUnlock = () =>
  new Promise((resolve) => {
    if (unlockTicket) return resolve(true);
    unlockWaiters.push(resolve);
  });

// Сигнал «покажи экран кода» из axios-интерцептора в React-дерево.
// reason описывает, ЧТО именно просит подтверждения: от него зависит и вид
// окна (лист против полного экрана), и подпись под заголовком.
const lockListeners = new Set();
export const onLockRequired = (cb) => {
  lockListeners.add(cb);
  return () => lockListeners.delete(cb);
};
export const requestLock = (reason = "") => {
  for (const cb of lockListeners) {
    try {
      cb(String(reason || ""));
    } catch {
      /* noop */
    }
  }
};

/**
 * Причина шаг-апа по адресу запроса. Разбор здесь, а не на страницах:
 * интерцептор — единственное место, где о 428 узнают все, и протаскивать
 * контекст через каждый вызов api.post ради подписи в одном окне незачем.
 */
export const unlockReasonForUrl = (url) => {
  const path = String(url || "");
  if (/\/cards\/[^/]+\/secure/.test(path)) return "card_secure";
  return "";
};

// Подавление перезапирания на время доверенных нативных оверлеев
// (QR-сканер Telegram прячет webview — это не «уход из приложения»).
// Ключи вместо счётчика: повторный end по тому же ключу безвреден.
const trustedOverlays = new Set();
export const beginTrustedOverlay = (reason = "default") => {
  trustedOverlays.add(reason);
};
export const endTrustedOverlay = (reason = "default") => {
  trustedOverlays.delete(reason);
};
export const hasTrustedOverlay = () => trustedOverlays.size > 0;

// 32 случайных байта -> base64url. Хранится в secure storage устройства
// через Telegram BiometricManager; сервер знает только HMAC от него.
export const generateBiometricSecret = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/**
 * Рантайм-проба BiometricManager. НЕ по isTelegramWebApp (там UA-снифинг) и
 * не через мок из контекста — только реальный window.Telegram.WebApp.
 * Возвращает { bm, available, type } либо { bm: null, available: false }.
 */
export const getBiometricManager = () =>
  new Promise((resolve) => {
    let bm;
    try {
      bm = window?.Telegram?.WebApp?.BiometricManager;
    } catch {
      bm = null;
    }
    if (!bm || typeof bm.init !== "function") {
      resolve({ bm: null, available: false, type: "unknown" });
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      let available = false;
      let type = "unknown";
      try {
        available = Boolean(bm.isBiometricAvailable);
        type = String(bm.biometricType || "unknown");
      } catch {
        /* noop */
      }
      resolve({ bm, available, type });
    };
    // init-колбэк может не прийти на старых клиентах — страхуемся таймаутом.
    const timer = setTimeout(finish, 1500);
    try {
      if (bm.isInited) {
        clearTimeout(timer);
        finish();
        return;
      }
      bm.init(() => {
        clearTimeout(timer);
        finish();
      });
    } catch {
      clearTimeout(timer);
      settled = true;
      resolve({ bm: null, available: false, type: "unknown" });
    }
  });

/** Достаёт detail.code из ответа бэка независимо от формы detail. */
export const errorCode = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") return String(detail.code || "");
  return "";
};

export const retryAfterSeconds = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    const value = Number(detail.retry_after_seconds);
    return Number.isFinite(value) && value > 0 ? Math.ceil(value) : 0;
  }
  return 0;
};
